import type { JobApplication, JobOfferClauses, JobOfferFields, JobPosting } from '../types/hiring';
import type { PayrollPosition, PayrollSettings } from '../types/finances';
import { DEFAULT_HOURS_PER_WEEK, DEFAULT_PAYROLL_SETTINGS } from '../types/finances';
import { deriveAnnualFromHourly } from './payrollCompSchedule';
import { defaultOfferClauses, defaultOfferDuties, defaultOfferFields } from './jobOfferLetter';

function isoDate(value: Date | string | null | undefined): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function sepPercent(position?: PayrollPosition | null, settings?: PayrollSettings | null): number | null {
  const rate = position?.sepIraRateOverride ?? settings?.sepIraRate ?? DEFAULT_PAYROLL_SETTINGS.sepIraRate;
  if (typeof rate !== 'number' || rate <= 0) return null;
  return Math.round(rate * 10000) / 100;
}

export function offerDefaultsFromHiring(input: {
  application: Pick<JobApplication, 'firstName' | 'lastName' | 'email' | 'jobTitle'>;
  posting?: Pick<JobPosting, 'title' | 'location' | 'employmentType'> | null;
  position?: PayrollPosition | null;
  settings?: PayrollSettings | null;
}): { fields: JobOfferFields; clauses: JobOfferClauses } {
  const { application, posting, position, settings } = input;
  const positionTitle = position?.title || posting?.title || application.jobTitle || 'Team Member';
  const payType = position?.payType === 'hourly' ? 'hourly' : 'salary';
  const hours = position?.hoursPerWeek || DEFAULT_HOURS_PER_WEEK;
  const payAmount = payType === 'hourly'
    ? (position?.hourlyRate || 0)
    : (position?.annualSalary || 0);
  const health = position?.healthReimbursementMonthly
    || settings?.defaultHealthReimbursementMonthly
    || null;
  const fields: JobOfferFields = {
    ...defaultOfferFields({ ...application, jobTitle: positionTitle }),
    positionTitle,
    duties: defaultOfferDuties(positionTitle),
    startDate: isoDate(position?.startDate),
    payType,
    payAmount,
    hoursPerWeek: hours,
    employmentType: posting?.employmentType || 'full-time',
    location: posting?.location?.trim() || defaultOfferFields(application).location,
  };
  const clauses: JobOfferClauses = {
    ...defaultOfferClauses(positionTitle, payType),
    sepIraPercent: sepPercent(position, settings),
    healthReimbursementMonthly: health && health > 0 ? health : null,
  };
  return { fields, clauses };
}

export function payrollPatchFromOffer(
  fields: JobOfferFields,
  clauses: JobOfferClauses,
): Record<string, unknown> {
  const hours = fields.hoursPerWeek || DEFAULT_HOURS_PER_WEEK;
  const annualSalary = fields.payType === 'hourly'
    ? deriveAnnualFromHourly(fields.payAmount, hours)
    : fields.payAmount;
  return {
    title: fields.positionTitle,
    payType: fields.payType,
    annualSalary,
    hourlyRate: fields.payType === 'hourly' ? fields.payAmount : null,
    hoursPerWeek: hours,
    startDate: fields.startDate ? new Date(`${fields.startDate}T12:00:00`) : undefined,
    healthReimbursementMonthly: clauses.healthReimbursementMonthly || 0,
    sepIraRateOverride: clauses.sepIraPercent != null ? clauses.sepIraPercent / 100 : null,
  };
}
